// src/main.tsx
import React, { useEffect, useState } from "react";
import ReactDOM from "react-dom/client";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { BrandingProvider } from "@/lib/branding";
import { initTheme } from "@/lib/theme";
import { ToastContainer, Spinner } from "@/components/shared";
import { getFunctions, httpsCallable } from "firebase/functions";
import "@/styles/globals.css";

// Apply saved theme preference before React renders to avoid a flash
// of the wrong theme on load.
initTheme();

// Layout
import AppShell from "@/components/layout/AppShell";

// Pages
import Login        from "@/pages/Login";
import Setup        from "@/pages/Setup";
import Dashboard    from "@/pages/Dashboard";
import Tickets      from "@/pages/Tickets";
import TicketDetail from "@/pages/TicketDetail";
import NewTicket    from "@/pages/NewTicket";
import AiChat       from "@/pages/AiChat";
import KnowledgeBase from "@/pages/KnowledgeBase";
import KbArticle    from "@/pages/KbArticle";
import KbImport     from "@/pages/KbImport";
import Analytics    from "@/pages/Analytics";
import Settings     from "@/pages/Settings";
import Preferences  from "@/pages/Preferences";
import CsatSurvey   from "@/pages/CsatSurvey";

// ─── Setup gate ───────────────────────────────────────────────────
// Hits the public getSetupState callable on first render. If no admin
// exists, redirects to /setup — except if we're already on /setup.

function SetupGate({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const [state, setState] = useState<"checking" | "ok" | "needs-setup">("checking");

  useEffect(() => {
    if (location.pathname === "/setup") { setState("ok"); return; }
    const fn = httpsCallable<unknown, { initialized: boolean }>(getFunctions(), "getSetupState");
    fn({}).then((res) => setState(res.data.initialized ? "ok" : "needs-setup"))
      .catch(() => setState("ok")); // fail open — don't lock users out if the call breaks
  }, [location.pathname]);

  if (state === "checking") {
    return <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center" }}><Spinner size={28} /></div>;
  }
  if (state === "needs-setup") return <Navigate to="/setup" replace />;
  return <>{children}</>;
}

// ─── Auth guard ───────────────────────────────────────────────────

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { firebaseUser, loading } = useAuth();

  if (loading) {
    return (
      <div style={{ height: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: "var(--bg)" }}>
        <div style={{ textAlign: "center" }}>
          <div style={{ width: 40, height: 40, background: "var(--blue)", borderRadius: 10, display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 12px" }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="white">
              <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
            </svg>
          </div>
          <p style={{ fontSize: 13, color: "var(--text2)" }}>Loading HelpDesk…</p>
        </div>
      </div>
    );
  }

  if (!firebaseUser) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

// ─── Role helpers ─────────────────────────────────────────────────
//
// Regular users have no queue-wide view. The Dashboard and All Tickets
// are agent surfaces — Firestore rules only let a user read tickets they
// submitted, so those screens can't show them anything anyway. Their home
// is My Tickets.

const AGENT_ROLES = ["agent", "admin"];

/** Where "home" is for this account. */
function useHomePath(): string {
  const { helpDeskUser } = useAuth();
  return helpDeskUser && !AGENT_ROLES.includes(helpDeskUser.role) ? "/my-tickets" : "/";
}

function HomeRedirect() {
  return <Navigate to={useHomePath()} replace />;
}

// ─── Admin/agent guard ────────────────────────────────────────────

function RequireAgent({ children }: { children: React.ReactNode }) {
  const { helpDeskUser } = useAuth();
  // helpDeskUser can be null for an account that isn't provisioned yet, or
  // if the Firestore read failed. Stay permissive there rather than risk
  // locking an admin out of Settings over a transient error — the server
  // is the real gate.
  if (helpDeskUser && !AGENT_ROLES.includes(helpDeskUser.role)) {
    return <Navigate to="/my-tickets" replace />;
  }
  return <>{children}</>;
}

// ─── Router ───────────────────────────────────────────────────────

function AppRoutes() {
  return (
    <Routes>
      <Route path="/setup" element={<Setup />} />
      <Route path="/login" element={<SetupGate><Login /></SetupGate>} />
      {/* Public CSAT survey — no auth required, validated by token */}
      <Route path="/csat" element={<BrandingProvider><CsatSurvey /></BrandingProvider>} />

      <Route path="/" element={
        <SetupGate>
          <RequireAuth>
            <BrandingProvider>
              <AppShell />
            </BrandingProvider>
          </RequireAuth>
        </SetupGate>
      }>
        <Route index element={<RequireAgent><Dashboard /></RequireAgent>} />
        <Route path="tickets"        element={<RequireAgent><Tickets /></RequireAgent>} />
        <Route path="tickets/new"    element={<NewTicket />} />
        <Route path="tickets/:id"    element={<TicketDetail />} />
        <Route path="my-tickets"     element={<Tickets />} />
        <Route path="chat"           element={<AiChat />} />
        <Route path="kb"             element={<KnowledgeBase />} />
        <Route path="kb/new"         element={<RequireAgent><KbArticle mode="new" /></RequireAgent>} />
        <Route path="kb/import"      element={<RequireAgent><KbImport /></RequireAgent>} />
        <Route path="kb/:id"         element={<KbArticle mode="view" />} />
        {/* Personal account settings — every role, no guard. */}
        <Route path="preferences"    element={<Preferences />} />
        <Route path="analytics"      element={<RequireAgent><Analytics /></RequireAgent>} />
        <Route path="settings"       element={<RequireAgent><Settings /></RequireAgent>} />
        <Route path="*"              element={<HomeRedirect />} />
      </Route>
    </Routes>
  );
}

// ─── Root ─────────────────────────────────────────────────────────

ReactDOM.createRoot(document.getElementById("root")!).render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        {/* First focusable element — keyboard users can press Tab once
            and skip past the sidebar nav to the main content area. */}
        <a href="#main-content" className="skip-link">Skip to main content</a>
        <AppRoutes />
        <ToastContainer />
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>
);
